/** @file Decodes PNG bytes into straight-alpha 8-bit RgbaImage; the inflate is capped by the header's size. */
import type { RgbaImage } from '@pose2d/math/types';
import { pngColor } from './_internal/pngColor';
import { pngInvalid } from './_internal/pngInvalid';
import { pngPasses, type PngPass } from './_internal/pngPasses';
import { readPngChunks } from './_internal/readPngChunks';
import { scanlinesToRgba } from './_internal/scanlinesToRgba';
import { unfilterScanlines } from './_internal/unfilterScanlines';
import { inflateExact, type InflateProblem } from './inflateExact';
import { PNG_LIMITS } from './pngLimits.data';
import { readPngIhdr, type PngIhdr } from './readPngIhdr';

/** The caller's caps (default PNG_LIMITS.default) and the error path (default `image`; the Worker upload uses `body`). */
export interface PngDecodeOptions {
  maxSide?: number;
  maxPixels?: number;
  path?: string;
}

/** Hint per inflate problem, given the expected and produced byte counts and the size, e.g. "1x1". */
const PROBLEM_HINTS: Record<InflateProblem, (expected: number, produced: number, size: string) => string> = {
  header: () => 'the image data is missing or is not a zlib stream (damaged IDAT chunks)',
  damaged: () => 'the compressed image data is damaged',
  overflow: (expected, _, size) => `the compressed image data inflates past the ${expected} bytes a ${size} PNG needs (a decompression bomb or a damaged file)`,
  short: (expected, produced, size) => `the image data holds only ${produced} of the ${expected} bytes a ${size} PNG needs`,
  truncated: (expected, produced, size) =>
    produced === expected
      ? 'the image data is cut off before its adler32 checksum (truncated file)'
      : `the image data ends after ${produced} of the ${expected} bytes a ${size} PNG needs (truncated file)`,
  trailing: () => 'the compressed image data continues past the end of its zlib stream (trailing bytes after the checksum)',
  checksum: () => 'the image data is damaged (its adler32 checksum does not match)',
};

/** The header policy before anything is allocated: sides of at least 1 px, the caller's limits, defined methods. */
function checkHeader(header: PngIhdr, options: PngDecodeOptions, path: string): void {
  const { width, height } = header;
  const { maxSide = PNG_LIMITS.default.maxSide, maxPixels = PNG_LIMITS.default.maxPixels } = options;
  if (width === 0 || height === 0) pngInvalid(path, `the image is ${width}x${height}; both sides must be at least 1 px`);
  if (Math.max(width, height) > maxSide || width * height > maxPixels) {
    pngInvalid(path, `the image is ${width}x${height}; at most ${maxSide} px a side and ${maxPixels} pixels are accepted`, { width, height, maxSide, maxPixels });
  }
  if (header.compression !== 0 || header.filter !== 0 || header.interlace > 1) {
    pngInvalid(path, 'the PNG header names a compression, filter or interlace method that does not exist', { ...header });
  }
}

/** Unfilters every pass in place; VALIDATION for a filter type PNG does not define. */
function unfilterPasses(data: Uint8Array, passes: readonly PngPass[], bitsPerPixel: number, path: string): void {
  let start = 0;
  for (const pass of passes) {
    const bad = unfilterScanlines(data, start, pass.height, pass.rowBytes, Math.max(1, bitsPerPixel >> 3));
    if (bad >= 0) pngInvalid(path, `the image data is damaged (scanline filter type ${bad} does not exist)`, { filter: bad });
    start += pass.height * (pass.rowBytes + 1);
  }
}

/**
 * Expands gray, gray+alpha, RGB and RGBA (8-bit, tRNS colour key) and palette images (1-8 bit indices, tRNS), plain
 * or Adam7, to RGBA. Order: IHDR (signature, CRC), zero sides, the caller's limits, every chunk's CRC, colour model,
 * then the inflate, capped at exactly the bytes the scanlines need. Throws VALIDATION at `options.path` for each.
 * Allocates, after the header has passed the limits: the scanline bytes (Σ rows × (1 + row bytes), (1 + 4w) × h for
 * 8-bit RGBA), the RGBA result (4·w·h), 8,640 bytes of inflate tables and a palette of at most 1 KiB, whatever the
 * stream would expand to. Measured on 2048² RGBA: 33,565,120 bytes allocated (16,779,264 + 16,777,216 + 8,640), so
 * peak memory is `bytes` + 32 MiB; a stream that would expand to 1 GiB stops after the 16 MiB of scanlines, ~23 ms.
 * The chunk walk keeps integers, never a value per chunk, and the inflate reads the IDAT payloads in place: a 20 MiB
 * file of ~1.6-1.7 M empty IDAT, one-byte IDAT or tRNS chunks decodes at +1-2 MiB peak RSS (0-1 MiB of JS heap).
 * Leniency: IDAT chunks need not be consecutive. Other chunks between them are skipped and the stream continues
 * in the next IDAT, as the old decoder did; libpng and Pillow refuse such files (PNG §5.6).
 */
export function decodePng(bytes: Uint8Array, options: PngDecodeOptions = {}): RgbaImage {
  const path = options.path ?? 'image';
  const header = readPngIhdr(bytes, path);
  checkHeader(header, options, path);
  const { width, height } = header;
  const chunks = readPngChunks(bytes, path, header);
  if (chunks.idatCount === 0) return pngInvalid(path, 'the PNG has no IDAT chunk (no image data)');
  const color = pngColor(header, chunks, path);
  const bitsPerPixel = color.bitDepth * color.channels;
  const passes = pngPasses(width, height, bitsPerPixel, header.interlace === 1);
  const expected = passes.reduce((n, p) => n + p.height * (p.rowBytes + 1), 0);
  const inflated = inflateExact({ png: bytes, first: chunks.firstIdat }, expected);
  if (!inflated.ok) {
    return pngInvalid(path, PROBLEM_HINTS[inflated.problem](expected, inflated.produced, `${width}x${height}`), { expected, produced: inflated.produced });
  }
  unfilterPasses(inflated.bytes, passes, bitsPerPixel, path);
  const data = scanlinesToRgba(inflated.bytes, passes, width, height, color);
  if (data === null) return pngInvalid(path, 'the palette indices are damaged (an index is outside PLTE or its bit depth is invalid)');
  return { width, height, data, premultiplied: false };
}
